// Runtime configuration loader.
//
// Loads /config.json (served as a static file by nginx) BEFORE the React app
// renders. Each environment's container writes its own /config.json at
// container start, so the same JS bundle can be promoted from stage → prod
// without rebuilding.
//
// Falls back to baked process.env.REACT_APP_* values if /config.json is
// missing — keeps `npm start` (CRA dev server) working without a config file.
//
// Usage:
//   // src/index.js
//   import { loadConfig } from './utils/runtimeConfig';
//   loadConfig().then(() => { ReactDOM.createRoot(...).render(<App />); });
//
//   // anywhere else (component / module that runs after render)
//   import { getConfig } from './utils/runtimeConfig';
//   const cfg = getConfig();
//   fetch(cfg.apiUrl + '/foo');
//
// IMPORTANT: getConfig() throws if called before loadConfig() resolves.
// Do NOT call it at module import time. Use it inside functions / component
// bodies, or wrap top-level reads in a lazy getter.

let _config = null;
let _loadPromise = null;

function bakedFallback() {
  return {
    apiUrl: process.env.REACT_APP_API_URL,
    wsUrl: process.env.REACT_APP_WS_URL,
    backendRootUrl: process.env.REACT_APP_BACKEND_ROOT_URL,
    websiteUrl: process.env.REACT_APP_WEBSITE_URL,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    firebase: {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
    },
  };
}

function normalize(raw) {
  // Accept either the new flat shape or backward-compat with REACT_APP_* keys.
  if (!raw || typeof raw !== 'object') return bakedFallback();
  const cfg = {
    apiUrl: raw.apiUrl || raw.REACT_APP_API_URL,
    wsUrl: raw.wsUrl || raw.REACT_APP_WS_URL,
    backendRootUrl:
      raw.backendRootUrl ||
      raw.REACT_APP_BACKEND_ROOT_URL ||
      raw.apiUrl ||
      raw.REACT_APP_API_URL,
    websiteUrl: raw.websiteUrl || raw.REACT_APP_WEBSITE_URL,
    googleMapsApiKey: raw.googleMapsApiKey || raw.REACT_APP_GOOGLE_MAPS_API_KEY,
    firebase: raw.firebase || {
      apiKey: raw.REACT_APP_FIREBASE_API_KEY,
      authDomain: raw.REACT_APP_FIREBASE_AUTH_DOMAIN,
      databaseURL: raw.REACT_APP_FIREBASE_DATABASE_URL,
      projectId: raw.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: raw.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: raw.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: raw.REACT_APP_FIREBASE_APP_ID,
      measurementId: raw.REACT_APP_FIREBASE_MEASUREMENT_ID,
    },
  };
  return cfg;
}

export async function loadConfig() {
  if (_config) return _config;
  if (_loadPromise) return _loadPromise;

  _loadPromise = fetch('/config.json', { cache: 'no-store' })
    .then((r) => {
      if (!r.ok) throw new Error(`/config.json HTTP ${r.status}`);
      return r.json();
    })
    .then((raw) => {
      _config = normalize(raw);
      return _config;
    })
    .catch((err) => {
      console.warn(
        '[runtimeConfig] /config.json unavailable, falling back to baked env vars:',
        err && err.message ? err.message : err
      );
      _config = bakedFallback();
      return _config;
    });

  return _loadPromise;
}

export function getConfig() {
  if (!_config) {
    throw new Error(
      '[runtimeConfig] getConfig() called before loadConfig() resolved. ' +
        'Wrap your app render in loadConfig().then(...) or call loadConfig() first.'
    );
  }
  return _config;
}

// Convenience helpers — these all throw if loadConfig() hasn't resolved.
export function getApiUrl() {
  return getConfig().backendRootUrl || getConfig().apiUrl || '/api';
}

export function getWsUrl() {
  const cfg = getConfig();
  if (cfg.wsUrl) return cfg.wsUrl;
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}/ws`;
  }
  return null;
}

export function getWebsiteUrl() {
  return getConfig().websiteUrl || 'https://www.confanum.com';
}

// Browser key for the venue map picker. Empty string when unset, so callers
// can simply hide the map UI instead of handling an error.
export function getGoogleMapsApiKey() {
  return (_config && _config.googleMapsApiKey) || '';
}

export function getFirebaseConfig() {
  return getConfig().firebase || {};
}
